import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { TrafficAnalysis } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard } from '../components/common'
import { formatBytes, CountSeries } from '../components/charts'
import { DataTable, type Column } from '../components/DataTable'
import { ElencoDispositivi } from '../components/ElencoDispositivi'
import { ElencoHostFilo } from '../components/ElencoHostFilo'
import { RiferimentoDispositivi } from '../components/RiferimentoDispositivi'
import { InventarioPassivo } from '../components/InventarioPassivo'
import { useAsync, usePolling } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Traffic Analysis: i numeri della raccolta e del riconoscimento in una
 * pagina sola. Non «cosa succede nella rete» (quello è Conversazioni) ma
 * «quanto stiamo vedendo, e quanto ne capiamo»: traffico decodificato,
 * pacchetti intercettati, dispositivi individuati e arricchiti — e ntopng,
 * quando c'è, con i suoi host e sistemi operativi.
 */
type Sonda = TrafficAnalysis['probes'][number]
type FirewallVisto = TrafficAnalysis['devices']['firewalls'][number]
type TopTalker = NonNullable<NonNullable<TrafficAnalysis['ntopng']>['hosts']>['top_talkers'][number]

export default function FlowAnalysis() {
  const [ore, setOre] = useState(24)
  // Il popup aperto da un numero: filtro (e valore) dell'elenco, col titolo.
  const [aperto, setAperto] = useState<{ titolo: string; filter: string; value?: string } | null>(null)
  const [hostAperti, setHostAperti] = useState<{ titolo: string; protocol: string } | null>(null)
  const apri = (titolo: string, filter: string, value?: string) => () => setAperto({ titolo, filter, value })
  const dati = usePolling(() => api.trafficAnalysis(ore), 60000, [ore])
  // CENSO MED: the medical device functions are a licence of their own.
  const med = useAsync(() => api.passiveInventory(), [])
  const conMed = Boolean(med.data?.licensed)
  // L'andamento del riconoscimento nel tempo (un punto al giorno): per capire
  // se migliora imparando firme, estendendo lo SPAN, mettendo agenti.
  const trend = useAsync(() => api.recognitionTrend(30), [])
  const [esito, setEsito] = useState<string | null>(null)
  const [erroreAzione, setErroreAzione] = useState<unknown>(null)
  // «Azzera avvistamenti»: dopo una correzione dell'ascolto (nomi sul
  // dispositivo sbagliato) si riparte puliti; le fonti ricostruiscono tutto
  // nei minuti successivi. Il server rifiuta chi non è amministratore.
  async function azzera() {
    if (!window.confirm(t('Azzerare tutti gli avvistamenti di questo spazio? Nomi, impronte e fonti sentite ripartono da zero; la sonda, l’agente DHCP e i firewall li ricostruiscono nei minuti successivi. Il catalogo MAC e le impronte imparate restano.'))) return
    setErroreAzione(null)
    try {
      const r = await api.clearSightings()
      setEsito(t('Azzerati {n} avvistamenti. Le fonti li ricostruiscono da sole: sonda ogni due minuti, agente DHCP ogni minuto, firewall ogni cinque.', { n: r.deleted }))
      dati.reload()
    } catch (e) {
      setErroreAzione(e)
    }
  }
  const d = dati.data
  const n = (v: number | null | undefined) => (v ?? 0).toLocaleString(localeCorrente())

  const testoCollector = (p: Sonda) =>
    `${p.flows.listening ? t('in ascolto') : t('spento')} · ${t('{n} pacchetti, {m} flussi', { n: n(p.flows.received_packets), m: n(p.flows.decoded_flows) })}`
  const testoCattura = (p: Sonda) =>
    p.capture
      ? `${p.capture.interface ?? ''} · ${p.capture.running ? t('attiva') : t('ferma')} · ${t('{n} pacchetti, {m} flussi, {d} scartati', { n: n(p.capture.packets), m: n(p.capture.flows), d: n(p.capture.dropped) })}`
      : ''
  const testoAscolto = (p: Sonda) =>
    p.listen
      ? `${p.listen.running ? t('in ascolto') : t('non in ascolto')} · ${p.listen.interfaces.join(', ')} · ${t('{n} annunci, {m} dispositivi', { n: n(p.listen.packets), m: n(p.listen.sightings) })}${p.listen.error ? ` · ${p.listen.error}` : ''}${p.listen.delivery_error ? ` · ${t('consegna al Control Plane non riuscita: {e}', { e: p.listen.delivery_error })}` : ''}${(p.listen.discarded ?? 0) + (p.listen.refused ?? 0) > 0 ? ` · ${t('{n} avvistamenti persi', { n: n((p.listen.discarded ?? 0) + (p.listen.refused ?? 0)) })}` : ''}`
      : ''
  const colonneSonde: Column<Sonda>[] = [
    {
      key: 'name',
      header: t('Sonda'),
      value: (p) => `${p.name}${p.silent ? ` ${t('silenziosa')}` : ''}`,
      render: (p) => (
        <><Link to={`/fleet/probes/${p.appliance_id}`}>{p.name}</Link>{p.silent && <> <span className="badge badge--warn">{t('silenziosa')}</span></>}</>
      ),
    },
    { key: 'collector', header: t('Collector'), value: testoCollector },
    {
      key: 'capture',
      header: t('Cattura'),
      value: testoCattura,
      render: (p) => (p.capture ? testoCattura(p) : <span className="text-muted">—</span>),
    },
    {
      key: 'listen',
      header: t('Ascolto passivo'),
      value: testoAscolto,
      render: (p) => (p.listen ? testoAscolto(p) : <span className="text-muted">—</span>),
    },
  ]

  // Each count opens the list of the devices it counts.
  const contaFirewall = (f: FirewallVisto, campo: 'paloalto-arp' | 'paloalto-dhcp' | 'fortigate', titolo: string) =>
    f[campo] ? (
      <button type="button" className="link" onClick={apri(titolo, 'source', `${campo}:${f.name}`)}>{n(f[campo])}</button>
    ) : (
      <span className="text-muted">—</span>
    )
  const colonneFirewall: Column<FirewallVisto>[] = [
    { key: 'name', header: t('Firewall') },
    {
      key: 'paloalto-arp',
      header: t('ARP'),
      type: 'number',
      value: (f) => f['paloalto-arp'] || null,
      render: (f) => contaFirewall(f, 'paloalto-arp', t('ARP di {f}', { f: f.name })),
    },
    {
      key: 'paloalto-dhcp',
      header: t('Lease DHCP'),
      type: 'number',
      value: (f) => f['paloalto-dhcp'] || null,
      render: (f) => contaFirewall(f, 'paloalto-dhcp', t('Lease DHCP di {f}', { f: f.name })),
    },
    {
      key: 'fortigate',
      header: t('Device inventory'),
      type: 'number',
      value: (f) => f.fortigate || null,
      render: (f) => contaFirewall(f, 'fortigate', t('Device inventory di {f}', { f: f.name })),
    },
  ]

  const colonneTalker: Column<TopTalker>[] = [
    { key: 'ip', header: 'IP', mono: true },
    { key: 'name', header: t('Nome') },
    { key: 'os', header: t('Sistema') },
    { key: 'bytes', header: t('Volume'), type: 'number', render: (h) => formatBytes(h.bytes) },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Traffic Analysis')}</h1>
          <p className="page__subtitle">{t('Mostra il volume di traffico rilevato, la percentuale decodificata e il numero di dispositivi identificati.')}</p>
        </div>
        <div className="page__actions">
          <select value={ore} onChange={(e) => setOre(Number(e.target.value))} aria-label={t('Finestra')}>
            <option value={1}>{t('ultima ora')}</option>
            <option value={24}>{t('ultime 24 ore')}</option>
            <option value={168}>{t('ultimi 7 giorni')}</option>
            <option value={720}>{t('ultimi 30 giorni')}</option>
          </select>
        </div>
      </header>
      <ErrorBanner error={dati.error} />
      {dati.loading && !d ? <Spinner /> : !d ? null : (
        <>
          {/* --- Traffico decodificato --- */}
          <Card title={t('Traffico decodificato')}>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('confronta i flussi registrati nel periodo selezionato con il traffico che le sonde dichiarano di aver ricevuto e decodificato dall’avvio, tramite NetFlow, IPFIX, sFlow e cattura di rete.')}
            </p>
            {d.traffic.error && <p className="field__hint">{t('Registro dei flussi non leggibile: {e}', { e: d.traffic.error })}</p>}
            <div className="stats">
              <StatCard label={t('Volume nella finestra')} value={formatBytes(d.traffic.bytes)} to="/flows" />
              <StatCard label={t('Pacchetti nella finestra')} value={n(d.traffic.packets)} />
              <StatCard label={t('Flussi nella finestra')} value={n(d.traffic.flows)} />
              <StatCard label={t('Pacchetti ricevuti dalle sonde')} value={n(d.totals.received_packets)} hint={t('dall’avvio, tutti i collector')} />
              <StatCard label={t('Flussi decodificati dalle sonde')} value={n(d.totals.decoded_flows)} hint={t('dall’avvio')} />
              <StatCard
                label={t('Esportatori')}
                value={`${n(d.exporters.total)}${d.exporters.silent ? ` (${d.exporters.silent} ${t('silenziosi')})` : ''}`}
                tone={d.exporters.silent ? 'warn' : undefined}
                to="/flows/exporters"
              />
            </div>
          </Card>

          {/* --- Pacchetti intercettati --- */}
          <Card title={t('Pacchetti intercettati')}>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('mostra, per ogni sonda, il traffico acquisito da una porta mirror e i dispositivi rilevati tramite protocolli di rete locali come ARP, DHCP, mDNS, SSDP, NetBIOS e LLDP.')}
            </p>
            <div className="stats">
              <StatCard label={t('Catturati dal filo')} value={n(d.totals.captured_packets)} hint={t('dall’avvio')} />
              <StatCard
                label={t('Scartati dal kernel')}
                value={n(d.totals.captured_dropped)}
                tone={d.totals.captured_dropped > 0 ? 'warn' : undefined}
                hint={t('la sonda non teneva il passo: i volumi sono incompleti')}
              />
              <StatCard label={t('Annunci sentiti (ascolto passivo)')} value={n(d.totals.listen_packets)} hint={t('dall’avvio')} />
              <StatCard label={t('Dispositivi sentiti')} value={n(d.totals.listen_sightings)} />
            </div>
            {d.probes.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <DataTable
                  rows={d.probes}
                  columns={colonneSonde}
                  rowKey={(p) => p.appliance_id}
                  conteggio={{ nome: t('sonde') }}
                  // A silent probe is shown dimmed.
                  rowClassName={(p) => (p.silent ? 'riga-sbiadita' : undefined)}
                />
              </div>
            )}
          </Card>

          {/* --- Dispositivi individuati e identificati --- */}
          <Card title={t('Dispositivi individuati e identificati')}>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('mostra gli indirizzi fisici (MAC) rilevati sulle porte degli switch e tramite ascolto passivo, arricchiti con le informazioni disponibili: produttore, tipo di dispositivo e identificazioni ottenute dal catalogo CENSO, Fingerbank e impronte di riconoscimento.')}
            </p>
            <div className="stats">
              <StatCard label={t('Dispositivi individuati')} value={n(d.devices.total)} hint={t('{a} sulle porte, {b} sentiti, {c} nelle ultime 24 ore', { a: n(d.devices.on_switch_ports), b: n(d.devices.heard), c: n(d.devices.heard_24h) })} onClick={apri(t('Dispositivi individuati'), 'all')} />
              <StatCard label={t('Sulle porte degli switch')} value={n(d.devices.on_switch_ports)} onClick={apri(t('Sulle porte degli switch'), 'on_switch_ports')} />
              <StatCard label={t('Sentiti (ascolto, DHCP, firewall)')} value={n(d.devices.heard)} hint={t('{c} nelle ultime 24 ore', { c: n(d.devices.heard_24h) })} onClick={apri(t('Sentiti (ascolto, DHCP, firewall)'), 'heard')} />
              <StatCard label={t('Con costruttore')} value={n(d.devices.with_vendor)} onClick={apri(t('Con costruttore'), 'with_vendor')} />
              <StatCard label={t('Riconosciuti dagli annunci')} value={n(d.devices.recognised_locally)} onClick={apri(t('Riconosciuti dagli annunci'), 'recognised')} />
              {conMed && <StatCard label={t('Riconosciuti dal prodotto')} value={n(d.devices.recognised_product)} hint={t('dal nome con cui il dispositivo si presenta')} onClick={apri(t('Riconosciuti dal prodotto'), 'product')} />}
              <StatCard label={t('Nel catalogo CENSO')} value={n(d.devices.in_catalogue)} hint={Object.entries(d.devices.catalogue_by_source).map(([k, v]) => `${etichettaOrigine(k)} ${v}`).join(' · ')} onClick={apri(t('Nel catalogo CENSO'), 'in_catalogue')} />
              <StatCard label={t('Elettromedicali')} value={n(d.devices.medical)} tone={d.devices.medical ? 'warn' : undefined} onClick={apri(t('Elettromedicali'), 'medical')} />
              <StatCard label={t('Segnalazioni aperte')} value={n(d.devices.reports.open)} hint={t('{c} catalogate, {s} scartate', { c: n(d.devices.reports.cataloged), s: n(d.devices.reports.dismissed) })} onClick={apri(t('Segnalazioni aperte'), 'reports_open')} />
            </div>
            <p className="field__hint">{t('Ogni valore identifica il numero dei dispositivi rilevati associati al CI e, dove disponibile, lo switch e la porta su cui sono stati rilevati.')}</p>
            <ErrorBanner error={erroreAzione} />
            {esito && <p className="field__hint">{esito}</p>}
            <div className="rowactions" style={{ textAlign: 'left' }}>
              <button type="button" className="secondary" onClick={() => void azzera()}
                      title={t('Cancella gli avvistamenti dell’ascolto passivo, dell’agente DHCP e dei firewall: da usare dopo una correzione, si ricostruiscono da soli.')}>
                {t('Azzera avvistamenti')}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 12 }}>
              <Tabella titolo={t('Tipi riconosciuti')} righe={d.devices.top_kinds.map((r) => [r.kind, r.count])} vuoto={t('Nessun tipo riconosciuto: accendi l’ascolto passivo sulla sonda.')} onApri={(k) => setAperto({ titolo: k, filter: 'kind', value: k })} />
              <Tabella titolo={t('Costruttori più frequenti')} righe={d.devices.top_vendors.map((r) => [r.vendor, r.count])} vuoto={t('Nessun dispositivo: lancia una scansione SNMP con «Tabella MAC» sugli switch.')} onApri={(k) => setAperto({ titolo: k, filter: 'vendor', value: k })} />
              <Tabella titolo={t('Per fonte')} righe={Object.entries(d.devices.by_source).map(([k, v]) => [etichettaFonte(k), v, k] as [string, number, string])} vuoto={t('Nessun avvistamento: accendi l’ascolto passivo sulla sonda, l’ascolto DHCP sull’agente del server DHCP o un firewall.')} onApri={(k, chiave) => setAperto({ titolo: etichettaFonte(chiave ?? k), filter: 'source', value: chiave ?? k })} />
            </div>
            <h4 style={{ margin: '16px 0 4px' }}>{t('Andamento del riconoscimento (30 giorni)')}</h4>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('Un punto al giorno: così si vede se il riconoscimento migliora imparando firme, estendendo lo SPAN o mettendo agenti sulle postazioni.')}
            </p>
            {trend.loading && !trend.data ? <Spinner /> : trend.error ? <ErrorBanner error={trend.error} /> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                <CountSeries label={t('Dispositivi individuati')} tone="accent"
                  points={(trend.data?.points ?? []).map((p) => ({ date: p.date, value: p.total }))} />
                <CountSeries label={t('Riconosciuti dagli annunci')} tone="ok"
                  points={(trend.data?.points ?? []).map((p) => ({ date: p.date, value: p.recognised }))} />
                {conMed && <CountSeries label={t('Elettromedicali')} tone="warn"
                  points={(trend.data?.points ?? []).map((p) => ({ date: p.date, value: p.medical }))} />}
              </div>
            )}
            {(() => {
              // La scala rispetto all'inventario di riferimento (Armis/Medigate):
              // «scoperto → identificato (tipo) → riconosciuto → stesso modello»,
              // giorno per giorno. Solo i giorni con un confronto (reference != null).
              const refPunti = (trend.data?.points ?? []).filter((p) => p.reference)
              if (refPunti.length === 0) return null
              const ultimo = refPunti[refPunti.length - 1].reference!
              const serie = (sel: (r: NonNullable<typeof ultimo>) => number) =>
                refPunti.map((p) => ({ date: p.date, value: sel(p.reference!) }))
              return (
                <>
                  <h4 style={{ margin: '20px 0 4px' }}>{t('Copertura rispetto all’inventario di riferimento')}</h4>
                  <p className="field__hint" style={{ marginTop: 0 }}>
                    {t('La scala del riconoscimento giorno per giorno, misurata sui {n} dispositivi dell’inventario di riferimento (Armis, Medigate…): scoperto, identificato come tipo, riconosciuto, fino allo stesso modello.').replace('{n}', String(ultimo.total))}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                    <CountSeries label={t('Scoperti sulla rete')} tone="accent"
                      points={serie((r) => r.visible)} />
                    <CountSeries label={t('Identificati (almeno il tipo)')} tone="warn"
                      points={serie((r) => r.visible - r.seen)} />
                    <CountSeries label={t('Riconosciuti (modello o elettromedicale)')} tone="ok"
                      points={serie((r) => r.recognised)} />
                    <CountSeries label={t('Stesso modello del riferimento')} tone="ok"
                      points={serie((r) => r.model)} />
                  </div>
                </>
              )
            })()}
          </Card>

          {/* --- CENSO MED: passive inventory and reference inventories --- */}
          {conMed ? (
            <>
              <InventarioPassivo />
              <RiferimentoDispositivi />
            </>
          ) : med.data && (
            <Card title={t('CENSO MED')}>
              <p className="field__hint" style={{ margin: 0 }}>
                {t('Non compreso nella licenza')}
              </p>
            </Card>
          )}

          {/* --- Protocolli sul filo: HTTP, TLS, SSH, SMB, DICOM, HL7, SIP, WS-Discovery --- */}
          <Card title={t('Protocolli sul filo')}>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('Analisi passiva del traffico: la sonda analizza il traffico acquisito da porta mirror o in modalità promiscua per identificare sistemi, servizi e applicazioni tramite HTTP, TLS, SSH, SMB, DICOM, HL7, SIP e WS-Discovery. I rilevamenti sono associati per IP al relativo CI e ogni valore apre l’elenco di dettaglio.')}
            </p>
            <div className="stats">
              <StatCard label={t('Host con fatti sul filo')} value={n(d.wire.hosts)} hint={d.wire.last_seen ? t('ultimo: {q}', { q: new Date(d.wire.last_seen).toLocaleString(localeCorrente()) }) : undefined} onClick={() => setHostAperti({ titolo: t('Host con fatti sul filo'), protocol: 'all' })} />
              <StatCard label={t('Riconosciuti dal filo')} value={n(d.wire.recognised)} onClick={() => setHostAperti({ titolo: t('Riconosciuti dal filo'), protocol: 'all' })} />
              <StatCard label={t('Medicali (DICOM / HL7)')} value={n(d.wire.medical)} tone={d.wire.medical ? 'warn' : undefined} onClick={() => setHostAperti({ titolo: t('Medicali (DICOM / HL7)'), protocol: 'medical' })} />
              <StatCard label={t('Con certificato TLS')} value={n(d.wire.with_cert)} onClick={() => setHostAperti({ titolo: t('Con certificato TLS'), protocol: 'tls' })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 12 }}>
              <Tabella titolo={t('Host per protocollo')} righe={Object.entries(d.wire.by_protocol).map(([k, v]) => [etichettaProtocollo(k), v, k] as [string, number, string])} vuoto={t('Niente ancora: serve una porta mirror o una scheda in promiscuo con l’ascolto passivo acceso.')} onApri={(k, chiave) => setHostAperti({ titolo: etichettaProtocollo(chiave ?? k), protocol: chiave ?? k })} />
            </div>
          </Card>

          {/* --- Dai firewall: ARP, lease DHCP, device inventory --- */}
          <Card title={t('Dai firewall')}>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('ogni cinque minuti acquisisce le informazioni sui dispositivi rilevati dai firewall. Rileva le associazioni MAC-IP per VLAN tramite ARP e DHCP, acquisisce tipo di dispositivo, sistema operativo e produttore. Ogni valore corrisponde all’elenco dei dispositivi rilevati.')}
            </p>
            {d.devices.firewalls.length === 0 ? (
              <EmptyState title={t('Nessun dispositivo dai firewall')} hint={t('Serve una credenziale Palo Alto o FortiGate in Integrazioni: la lettura parte da sola al primo giro.')} />
            ) : (
              <DataTable
                rows={d.devices.firewalls}
                columns={colonneFirewall}
                rowKey={(f) => f.name}
                conteggio={{ nome: t('firewall') }}
              />
            )}
          </Card>

          {/* --- ntopng --- */}
          {d.ntopng && (
            <Card title="ntopng">
              {!d.ntopng.available ? (
                <EmptyState title={t('ntopng non risponde')} hint={d.ntopng.error ?? ''} />
              ) : (
                <>
                  {d.ntopng.interface && (
                    <div className="stats">
                      <StatCard label={t('Host visti')} value={n(d.ntopng.interface.num_hosts)} hint={t('{n} locali', { n: n(d.ntopng.interface.num_local_hosts) })} />
                      <StatCard label={t('Flussi attivi')} value={n(d.ntopng.interface.num_flows)} />
                      <StatCard label={t('Pacchetti')} value={n(d.ntopng.interface.packets)} hint={d.ntopng.interface.drops ? t('{n} scartati', { n: n(d.ntopng.interface.drops) }) : undefined} tone={d.ntopng.interface.drops ? 'warn' : undefined} />
                      <StatCard label={t('Volume')} value={formatBytes(d.ntopng.interface.bytes ?? 0)} hint={d.ntopng.interface.bps != null ? `${formatBytes(Math.round(d.ntopng.interface.bps / 8))}/s` : undefined} />
                    </div>
                  )}
                  {d.ntopng.hosts && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 12 }}>
                      <Tabella titolo={t('Sistemi operativi (host locali)')} righe={d.ntopng.hosts.os.map((r) => [r.os, r.count])} vuoto={t('ntopng non ha ancora riconosciuto sistemi operativi.')} />
                      <Tabella titolo={t('Tipi di dispositivo (ntopng)')} righe={d.ntopng.hosts.device_types.map((r) => [r.type, r.count])} vuoto={t('ntopng non riporta tipi di dispositivo.')} />
                      <div>
                        <h4 style={{ margin: '0 0 6px', fontSize: 14 }}>{t('Top talker locali')}</h4>
                        <DataTable
                          rows={d.ntopng.hosts.top_talkers}
                          columns={colonneTalker}
                          rowKey={(h, i) => h.ip ?? h.mac ?? String(i)}
                          strumenti={false}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}
        </>
      )}
      {aperto && <ElencoDispositivi titolo={aperto.titolo} filter={aperto.filter} value={aperto.value} onChiudi={() => setAperto(null)} />}
      {hostAperti && <ElencoHostFilo titolo={hostAperti.titolo} protocol={hostAperti.protocol} onChiudi={() => setHostAperti(null)} />}
    </>
  )
}

/** Il nome leggibile di un protocollo del filo. */
function etichettaProtocollo(k: string): string {
  const nomi: Record<string, string> = {
    http: 'HTTP', tls: 'TLS', ssh: 'SSH', smb: 'SMB / NTLM', dicom: 'DICOM', hl7: 'HL7', sip: 'SIP', wsd: 'WS-Discovery',
  }
  return nomi[k] ?? k
}

/** Il nome leggibile di una fonte degli avvistamenti. */
function etichettaFonte(k: string): string {
  const base = k.split(':')[0]
  const nomi: Record<string, string> = {
    arp: t('ARP (sonda)'),
    dhcp: t('DHCP (sonda)'),
    mdns: 'mDNS',
    ssdp: 'SSDP',
    netbios: 'NetBIOS',
    lldp: 'LLDP',
    announce: t('Annunci in broadcast/multicast (sonda)'),
    'dhcp-server': t('Richieste DHCP (agente sul server DHCP)'),
    'dhcp-lease': t('Lease DHCP (agente sul server DHCP)'),
    'paloalto-arp': t('ARP del firewall Palo Alto'),
    'paloalto-dhcp': t('Lease DHCP del firewall Palo Alto'),
    fortigate: t('Device inventory FortiGate'),
    wifi: t('Client Wi-Fi (dagli access point)'),
    'arp-router': t('ARP dei router (SNMP)'),
  }
  const nome = nomi[base] ?? base
  return k.includes(':') ? `${nome} · ${k.split(':').slice(1).join(':')}` : nome
}

function etichettaOrigine(k: string): string {
  if (k === 'report') return t('da segnalazione')
  if (k === 'fingerbank') return 'Fingerbank'
  if (k === 'fingerprint') return t('stessa impronta')
  if (k === 'reference') return t('da inventario di riferimento')
  return t('a mano')
}

function Tabella({ titolo, righe, vuoto, onApri }: {
  titolo: string
  /** [etichetta, conteggio, chiave opzionale da passare a onApri]. */
  righe: ([string, number] | [string, number, string])[]
  vuoto: string
  /** Se c'è, ogni riga si apre sulla sua lista. */
  onApri?: (etichetta: string, chiave?: string) => void
}) {
  return (
    <div>
      <h4 style={{ margin: '0 0 6px', fontSize: 14 }}>{titolo}</h4>
      {righe.length === 0 ? (
        <p className="text-muted" style={{ margin: 0, fontSize: 13 }}>{vuoto}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <tbody>
              {righe.map(([k, v, chiave]) => (
                <tr
                  key={k}
                  className={onApri ? 'riga-apribile' : undefined}
                  onClick={onApri ? () => onApri(k, chiave) : undefined}
                  title={onApri ? t('Apri la lista') : undefined}
                >
                  <td>{k}</td><td style={{ textAlign: 'right' }}>{v.toLocaleString(localeCorrente())}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
